import { toast } from 'sonner';
import { type FormEvent, useEffect, useState } from 'react';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { NoPermission, usePermissions } from '@/features/permissions';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { summarizeVisit, type VisitScenario, visitSettings } from './eligibilitySummary';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Benefit = {
  type: string;
  network: string;
  services: string[];
  coverage_level: string;
  period: string;
  amount: string | null;
  percent: string | null;
  authorization: string;
  notes: string[];
};
type Result = {
  failure?: string;
  mode: string;
  checked_at: string;
  reference?: string;
  coverage: { status?: string; statusCode?: string; serviceTypeCodes?: string[] }[];
  plan_dates: Record<string, string>;
  benefits: Benefit[];
  errors: { code?: string; message: string }[];
  has_oon_details: boolean;
  notice: string;
};
type StediConfig = {
  /** Deployment allows real checks at all. Ours to change, not the practice's. */
  real_allowed: boolean;
  production_enabled: boolean;
  production_key_set: boolean;
  test_enabled: boolean;
};
export function EligibilityPage() {
  const { organization } = useAuth();
  return <Workspace key={organization?.organization_id} orgId={organization?.organization_id} />;
}
function Field({
  name,
  label,
  type = 'text',
  required = true,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} type={type} autoComplete="off" required={required} />
    </div>
  );
}

function Workspace({ orgId }: { orgId?: string }) {
  const { can } = usePermissions();
  const [config, setConfig] = useState<StediConfig | null>(null);
  const [keys, setKeys] = useState({ test: '', production: '' });
  const [savingKeys, setSavingKeys] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [dependent, setDependent] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [visit, setVisit] = useState<VisitScenario>({ cost: '', codes: '', setting: 'unspecified' });
  const summary = result ? summarizeVisit(result, visit) : null;
  const base = `/api/v1/organizations/${orgId}/eligibility`;
  useEffect(() => {
    let active = true;
    if (orgId && can('manage_claim'))
      clientApi
        .get(`${base}/configuration`)
        .then((r) => {
          if (active) setConfig(r.data);
        })
        .catch(() => {
          if (active) setError('Unable to load eligibility configuration.');
        });
    return () => {
      active = false;
    };
  }, [base, orgId, can]);
  if (!can('manage_claim')) return <NoPermission />;

  async function saveKeys() {
    setSavingKeys(true);
    setError('');
    try {
      const body: Record<string, string> = {};
      if (keys.test.trim()) body.test_api_key = keys.test.trim();
      if (keys.production.trim()) body.production_api_key = keys.production.trim();
      await clientApi.put(`${base}/credentials`, body);
      setKeys({ test: '', production: '' });
      setConfig((await clientApi.get<StediConfig>(`${base}/configuration`)).data);
      toast.success('Stedi keys saved');
    } catch {
      setError('Unable to save the Stedi keys.');
    } finally {
      setSavingKeys(false);
    }
  }

  async function send(path: string, body?: unknown) {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await clientApi.post<Result>(`${base}/${path}`, body);
      setResult(response.data);
    } catch {
      setError(
        'Eligibility request failed. Check the form fields and provider information. No coverage determination was made.',
      );
    } finally {
      setBusy(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const v = (name: string) => String(form.get(name) || '').trim();
    const person = (prefix: string) => ({
      last_name: v(`${prefix}_last`),
      first_name: v(`${prefix}_first`),
      date_of_birth: v(`${prefix}_dob`),
    });
    void send('check', {
      payer_id: v('payer_id'),
      member_id: v('member_id'),
      provider_npi: v('provider_npi'),
      service_date: v('service_date'),
      subscriber: person('subscriber'),
      provider_name: v('provider_name'),
      authorized: form.get('authorized') === 'on',
      dependent: dependent ? person('dependent') : null,
    });
  }
  const benefits =
    result?.benefits?.filter((b) => showAll || ['In and out of network', 'Out of network'].includes(b.network)) || [];
  return (
    <main className="flex w-full max-w-6xl flex-col gap-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Eligibility & benefits</h1>
        <p>Check general medical coverage and review the benefits reported by the insurer.</p>
      </div>
      {/*
        Keys belong to the practice, not the deployment. Before this, one key in
        the server's environment served every tenant, so every clinic's coverage
        checks billed to whoever owned it and queried payers under another
        practice's identity.
      */}
      <Card>
        <CardHeader>
          <CardTitle>Your Stedi account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Eligibility runs through Stedi, a clearinghouse. Your practice uses its own Stedi account, so checks are
            billed to you and payers see your identity. Get both keys from your Stedi dashboard and paste them here.
            They are stored encrypted and never shown again.
          </p>
          <div className="grid gap-4 md:max-w-xl">
            <div className="flex flex-col gap-2">
              <Label htmlFor="stedi-test">
                Sandbox key {config?.test_enabled && <span className="text-muted-foreground">(set)</span>}
              </Label>
              <Input
                id="stedi-test"
                type="password"
                value={keys.test}
                autoComplete="off"
                placeholder={config?.test_enabled ? 'Replace the stored sandbox key' : 'Runs checks against a sample patient'}
                onChange={(e) => setKeys({ ...keys, test: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="stedi-prod">
                Production key {config?.production_key_set && <span className="text-muted-foreground">(set)</span>}
              </Label>
              <Input
                id="stedi-prod"
                type="password"
                autoComplete="off"
                value={keys.production}
                placeholder={config?.production_key_set ? 'Replace the stored production key' : 'Queries real payers'}
                onChange={(e) => setKeys({ ...keys, production: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                A real check sends a patient&rsquo;s name, date of birth and member ID to Stedi. Make sure your
                agreement with them covers that before you use it.
              </p>
            </div>
          </div>
          <Button disabled={savingKeys || !(keys.test.trim() || keys.production.trim())} onClick={saveKeys}>
            {savingKeys ? 'Saving…' : 'Save keys'}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Try a synthetic check</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/*
            Name the missing thing and where to put it. A disabled control with
            no explanation is a dead end, and these two keys are easy to
            confuse: holding the production key does nothing for this button.
          */}
          {config && !config.test_enabled ? (
            <p>
              Not set up yet. This needs your practice&rsquo;s Stedi <strong>sandbox</strong> key, which is a different
              key from the one real coverage checks use. Add it under &ldquo;Your Stedi account&rdquo; above.
            </p>
          ) : (
            <>
              <p>Uses Stedi’s documented sample patient. Your form entries are not included.</p>
              <Button disabled={busy} onClick={() => send('test')}>
                {busy ? 'Checking…' : 'Run synthetic eligibility check'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Check real coverage</CardTitle>
        </CardHeader>
        <CardContent>
          {!config?.production_enabled ? (
            // Two different refusals with two different remedies, so they read
            // differently. One the practice fixes; the other is ours.
            <p>
              {config && !config.real_allowed
                ? 'Real coverage checks are switched off for this deployment. Contact MohairNotes support.'
                : 'Not set up yet. Add your practice’s Stedi production key under “Your Stedi account” above.'}
            </p>
          ) : (
            <form autoComplete="off" className="flex flex-col gap-6" onSubmit={submit}>
              <fieldset className="flex flex-col gap-4" aria-describedby="visit-context-note">
                <legend className="mb-3 font-semibold">Anticipated out-of-network visit</legend>
                <Label htmlFor="visit_codes">CPT / HCPCS codes (comma-separated)</Label>
                <Input
                  id="visit_codes"
                  maxLength={200}
                  value={visit.codes}
                  placeholder="e.g., 99205"
                  pattern="[A-Za-z0-9]{5}([, ]+[A-Za-z0-9]{5})*"
                  title="Enter five-character codes separated by commas or spaces."
                  onChange={(e) => setVisit({ ...visit, codes: e.target.value })}
                />
                <Label htmlFor="visit_setting">Visit setting</Label>
                <Select value={visit.setting} onValueChange={(setting) => setVisit({ ...visit, setting })}>
                  <SelectTrigger id="visit_setting">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {Object.entries(visitSettings).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Label htmlFor="visit_cost">Total anticipated charge (USD, all procedures)</Label>
                <Input
                  id="visit_cost"
                  min="0"
                  step="0.01"
                  type="number"
                  value={visit.cost}
                  placeholder="e.g., 200.00"
                  onChange={(e) => setVisit({ ...visit, cost: e.target.value })}
                />
                <p id="visit-context-note" className="text-sm text-muted-foreground">
                  Optional details for your plain-language summary. You can change them after a check without another
                  lookup. They are used on this page only; the insurer receives a general coverage inquiry, not a
                  procedure-specific price request.
                </p>
              </fieldset>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field name="payer_id" label="Verified Stedi payer ID" />
                <Field name="provider_name" label="Requesting provider / practice name" />
                <Field name="provider_npi" label="Requesting provider NPI" />
                <Field type="date" name="service_date" label="Date of service" />
              </div>
              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-3 font-semibold">Subscriber</legend>
                <Field name="member_id" label="Insurance member ID" />
                <Field name="subscriber_first" label="Subscriber first name" />
                <Field name="subscriber_last" label="Subscriber last name" />
                <Field type="date" name="subscriber_dob" label="Subscriber date of birth" />
              </fieldset>
              <label className="flex gap-3">
                <input type="checkbox" checked={dependent} onChange={(e) => setDependent(e.target.checked)} />
                The patient is a dependent identified through the subscriber’s policy
              </label>
              {dependent && (
                <fieldset className="grid gap-4 sm:grid-cols-3">
                  <legend className="mb-3 font-semibold">Dependent patient</legend>
                  <Field name="dependent_first" label="Patient first name" />
                  <Field name="dependent_last" label="Patient last name" />
                  <Field type="date" name="dependent_dob" label="Patient date of birth" />
                </fieldset>
              )}
              <p>
                This sends a general medical benefits inquiry (service type 30). Use the requesting provider’s actual
                identity and confirm any required payer enrollment.
              </p>
              <label className="flex items-start gap-3">
                <input required type="checkbox" name="authorized" />I authorize sending the entered identity, insurance,
                provider, and service-date information to Stedi and the selected insurer for this eligibility lookup. I
                am authorized to request these benefits and understand the account’s lookup charges may apply.
              </label>
              <Button type="submit" disabled={busy}>
                {busy ? 'Checking…' : 'Send real eligibility request'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      {(error || result?.failure) && (
        <Alert variant="destructive">
          <AlertTitle>Check needs attention</AlertTitle>
          <AlertDescription>{error || result?.failure}</AlertDescription>
        </Alert>
      )}
      {result && !result.failure && (
        <Card>
          <CardHeader>
            <CardTitle>
              {result.mode === 'test' ? 'Synthetic benefits — not your coverage' : 'Reported eligibility findings'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {summary && (
              <section aria-label="Plain-language cost summary">
                <Alert>
                  <AlertTitle>What this means for your anticipated cost</AlertTitle>
                  <AlertDescription className="flex flex-col gap-3">
                    <p>{summary.scenario}</p>
                    <p>
                      <strong>{summary.title}</strong>
                    </p>
                    <p>{summary.finding}</p>
                    <p>
                      <strong>{summary.budget}</strong>
                    </p>
                    {summary.followUp.length > 0 && (
                      <div>
                        <p>
                          <strong>Before relying on reimbursement</strong>
                        </p>
                        <ul className="list-disc pl-5">
                          {summary.followUp.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.mode !== 'test' && (
                      <p className="text-sm">
                        Based on the current visit details above and the general benefits returned by this check. No
                        procedure-specific payment quote was returned.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              </section>
            )}
            <p>
              Checked {new Date(result.checked_at).toLocaleString()}{' '}
              {result.reference && `· Reference ${result.reference}`}
            </p>
            <Alert>
              <AlertTitle>How to interpret these findings</AlertTitle>
              <AlertDescription>{result.notice}</AlertDescription>
            </Alert>
            {result.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle>Payer reported issues</AlertTitle>
                <AlertDescription>
                  {result.errors.map((e) => `${e.code || ''} ${e.message}`).join('; ')}
                </AlertDescription>
              </Alert>
            )}
            <div>
              <h2 className="font-semibold">Coverage returned by the payer</h2>
              {result.coverage.length ? (
                result.coverage.map((c, i) => (
                  <p key={i}>
                    {c.status || c.statusCode || 'Not specified'} · Service types:{' '}
                    {c.serviceTypeCodes?.join(', ') || 'Not specified'}
                  </p>
                ))
              ) : (
                <p>No plan-level coverage status was returned.</p>
              )}
            </div>
            {!result.has_oon_details && (
              <p>
                No explicitly labeled out-of-network benefits were returned. This does not establish that out-of-network
                coverage is unavailable.
              </p>
            )}
            <label className="flex gap-3">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Show all reported benefits, including in-network and unspecified
            </label>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {[
                      'Benefit / service',
                      'Network',
                      'Coverage level',
                      'Period',
                      'Patient responsibility',
                      'Notes',
                    ].map((h) => (
                      <th key={h} className="p-3 text-left">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {benefits.map((b, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-3">
                        {b.type}
                        <div>{b.services.join(', ')}</div>
                      </td>
                      <td className="p-3">{b.network}</td>
                      <td className="p-3">{b.coverage_level}</td>
                      <td className="p-3">{b.period}</td>
                      <td className="p-3">
                        {b.amount != null
                          ? `$${b.amount}`
                          : b.percent != null
                            ? `${(Number(b.percent) * 100).toFixed(2)}%`
                            : 'Not reported'}
                      </td>
                      <td className="p-3">
                        {b.notes.join(' ')}
                        {b.authorization !== 'Not specified' && ` Authorization: ${b.authorization}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
}
